import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Filter, SortAsc, SortDesc, Eye, X, Edit, Trash2, AlertCircle, List, ChevronLeft, ArrowLeftCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../../components/ui/pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from "react-hot-toast";
import { createCompany, getAllCompanies, updateCompany, getCompanyListForSelection } from '../../services/companiesapi';
import { uploadFile } from '../../services/usersapi';

// Update the Company interface to match backend response
interface Company {
  id: string;
  name: string;
  email: string;
  phone: string;
  operationalAddress?: string;
  registeredAddress?: string;
  gstNo?: string;
  logoUrl?: string;
  spocDetails: ISPOCDetailsDTO[];
  createdAt: string;
  onboardedBy?: {
    id: string;
    username: string;
    email: string;
  };
  referredBy?: {
    id: string;
    username: string;
    email: string;
  };
  onboardingFranchiseId?: string;
  onboardedByUserId?: string;
  openings?: number;
  candidates?: number;
  brand: string;
  website: string;
  isExternalCompany?: boolean;
}

// Define SPOC details DTO
export interface ISPOCDetailsDTO {
  name: string;
  email: string;
  phone: string;
  location: string;
}

// Add interface for company selection
interface ICompanySelectionItem {
  id: string;
  name: string;
  logoUrl?: string;
  isExternalCompany?: boolean;
}

// Define form data interface
interface FormData {
  companyName: string;
  address: string;
  email: string;
  phone: string;
  gstTaxNumber: string;
  spocDetails: ISPOCDetailsDTO[];
  logo: File | null;
  website: string;
  brandName: string;
  isExternalCompany: boolean;
}
const Companies: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    address: '',
    email: '',
    phone: '',
    gstTaxNumber: '',
    spocDetails: [{ name: '', email: '', phone: '', location: '' }],
    logo: null,
    website: '',
    brandName: '',
    isExternalCompany: false
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [ loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [updateFormData, setUpdateFormData] = useState({
    companyName: '',
    address: '',
    email: '',
    phone: '',
    gstTaxNumber: '',
    spocDetails: [{ name: '', email: '', phone: '', location: '' }],
    logo: null as File | null,
    website: '',
    removeCurrentLogo: false,
    brandName: '',
    isExternalCompany: false
  });
  const [selectionListOpen, setSelectionListOpen] = useState(false);
  const [companySelectionList, setCompanySelectionList] = useState<ICompanySelectionItem[]>([]);
  const [allCompanySelectionList, setAllCompanySelectionList] = useState<ICompanySelectionItem[]>([]);
  const [selectionListLoading, setSelectionListLoading] = useState(false);
  const [selectionListError, setSelectionListError] = useState<string | null>(null);
  const [selectionSearchTerm, setSelectionSearchTerm] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState<'all' | 'external' | 'internal'>('all');
  const [totalPages, setTotalPages] = useState(1);
  const [addCompanyBtnDisabled, setAddCompanyBtnDisabled] = useState(false)
  // Debounced search function
  const debounceSearch = useCallback(
    (searchTerm: string) => {
      const timeoutId = setTimeout(() => {
        (async () => {
          try {
            setSelectionListLoading(true);
            const response = await getCompanyListForSelection(); // No argument, matches API signature
            if (response && response.data && Array.isArray(response.data.companies)) {
              setCompanySelectionList(response.data.companies);
            }
          } catch (err) {
            setSelectionListError('Failed to search companies');
          } finally {
            setSelectionListLoading(false);
          }
        })();
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    []
  );

  // Effect for debounced search
  useEffect(() => {
    const cleanup = debounceSearch(selectionSearchTerm);
    return cleanup;
  }, [selectionSearchTerm, debounceSearch]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("🔄 Fetching companies...");
        const params = {
        page: currentPage.toString(),
        search: searchTerm,
        companyType: companyTypeFilter,
      };


       
        const response = await getAllCompanies(params);
        console.log("📥 Raw API Response:", response);
       
        if (response && response.data) {
          if (Array.isArray(response.data.companies)) {
            const mappedCompanies = response.data.companies.map((company: any) => ({
              ...company,
              name: company.name || '',
              email: company.email || '',
              phone: company.phone || '',
              operationalAddress: company.operationalAddress || '',
              registeredAddress: company.registeredAddress || '',
              openings: company.openings || 0,
              candidates: company.candidates || 0,
            }));
           
            console.log("✅ Mapped companies:", mappedCompanies);
            setCompanies(mappedCompanies);
            setTotalPages(response.totalPages || 1);
          } else if (Array.isArray(response.data)) {
            console.log("📋 Companies in data array:", response.data);
            setCompanies(response.data);
          } else {
            console.error("❌ Unexpected response structure:", response);
            setError("Invalid response format from server");
            setCompanies([]);
          }
        } else {
          console.error("❌ No data in response:", response);
          setError("No data received from server");
          setCompanies([]);
        }
      } catch (err: any) {
        console.error("❌ API Error:", err);
        console.error("Error details:", err.response?.data || err.message);
        setError(err.response?.data?.message || err.message || 'Failed to fetch companies');
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, [currentPage,searchTerm,companyTypeFilter]);

  useEffect(() => {
    console.log("Companies state changed:", companies);
    console.log("Number of companies:", companies?.length || 0);
  }, [companies]);

  const fetchCompanySelectionList = async () => {
    try {
      setSelectionListLoading(true);
      setSelectionListError(null);
      console.log("Fetching company selection list...");
     
      const response = await getCompanyListForSelection();
     
      if (response && response.data && Array.isArray(response.data.companies)) {
        setAllCompanySelectionList(response.data.companies);
        setCompanySelectionList(response.data.companies);
        console.log("Company selection list loaded:", response.data.companies.length, "companies");
      } else {
        console.error("Unexpected selection list response structure:", response);
        setAllCompanySelectionList([]);
        setCompanySelectionList([]);
        setSelectionListError('Invalid response format');
      }
    } catch (err: any) {
      console.error("Failed to load company selection list:", err);
      setSelectionListError(err.message || 'Failed to fetch company selection list');
      setAllCompanySelectionList([]);
      setCompanySelectionList([]);
    } finally {
      setSelectionListLoading(false);
    }
  };

  const handleOpenSelectionList = () => {
    setSelectionListOpen(true);
    fetchCompanySelectionList();
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredByType = Array.isArray(companies)
    ? companies.filter(company => {
        if (companyTypeFilter === 'external') {
          return !!company.isExternalCompany;
        }
        if (companyTypeFilter === 'internal') {
          return !company.isExternalCompany;
        }
        return true;
      })
    : [];

  const filteredCompanies = filteredByType.filter(company =>
    (company.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.operationalAddress || company.registeredAddress || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCompanyTypeChange = (value: string) => {
    const nextValue = value as 'all' | 'external' | 'internal';
    setCompanyTypeFilter(nextValue);
    setCurrentPage(1);
  };

  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    let aValue: any;
    let bValue: any;
   
    switch (sortBy) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      default:
        aValue = (a as any)[sortBy] || '';
        bValue = (b as any)[sortBy] || '';
    }
   
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = sortedCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || loading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={selectionListLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={selectionListLoading}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || selectionListLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>, spocIndex?: number) => {
    const { name, value, files } = e.target;
    
    if (name === 'logo' && files && files[0]) {
      try {
        const loadingToast = toast.loading('Uploading logo...');
        
        // Validate file
        const file = files[0];
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
          toast.dismiss(loadingToast);
          toast.error('Invalid file type. Only JPG, PNG, GIF, and WebP images are allowed.');
          e.target.value = '';
          return;
        }

        if (file.size > maxSize) {
          toast.dismiss(loadingToast);
          toast.error('File size exceeds 5MB limit');
          e.target.value = '';
          return;
        }

        // Generate a temporary ID for new companies
        const tempId = 'temp-' + Date.now();
        // Use company ID in the folder path
        const folderPath = `company/${tempId}/logo`;

        // Upload file with the new folder path
        const response = await uploadFile(file, folderPath);
        
        if (response && response.fileUrl) {
          setFormData(prev => ({ 
            ...prev, 
            logo: file,
            logoUrl: response.fileUrl 
          }));
          
          toast.dismiss(loadingToast);
          toast.success('Logo uploaded successfully');
        }
      } catch (error: any) {
        toast.error(`Failed to upload logo: ${error.message}`);
        e.target.value = '';
      }
    } else if (spocIndex !== undefined) {
      setFormData(prev => ({
        ...prev,
        spocDetails: prev.spocDetails.map((spoc, index) =>
          index === spocIndex ? { ...spoc, [name]: value } : spoc
        ),
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addSpoc = () => {
    setFormData(prev => ({
      ...prev,
      spocDetails: [...prev.spocDetails, { name: '', email: '', phone: '', location: '' }],
    }));
  };

  const removeSpoc = (index: number) => {
    setFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.companyName?.trim()) {
      setError('Company name is required');
      toast.error('Company name is required');
      return;
    }
    if (!formData.address?.trim()) {
      setError('Address is required');
      toast.error('Address is required');
      return;
    }
    
    // Skip email and phone validation for external companies
    if (!formData.isExternalCompany) {
      if (!formData.email?.trim()) {
        setError('Email is required');
        toast.error('Email is required');
        return;
      }
      if (!formData.phone?.trim()) {
        setError('Phone is required');
        toast.error('Phone is required');
        return;
      }
      // Phone number should only contain digits
      if (!/^[0-9]+$/.test(formData.phone.trim())) {
        toast.error('Phone must contain only numbers');
        return;
      }
    }
    
    if (!formData.brandName?.trim()) {
      setError('Brand is required');
      toast.error('Brand is required');
      return;
    }
    if (!formData.website?.trim()) {
      setError('website is required');
      toast.error('website is required');
      return;
    }
    
    // Duplicate validation - skip email/phone check for external companies
    const duplicateCompany = companies.find(
      (c) =>
        c.name.trim().toLowerCase() === formData.companyName.trim().toLowerCase() ||
        (!formData.isExternalCompany && c.email.trim().toLowerCase() === formData.email.trim().toLowerCase()) ||
        (!formData.isExternalCompany && c.phone.trim() === formData.phone.trim()) ||
        c.brand.trim().toLowerCase() === formData.brandName.toLowerCase() ||
        c.website.trim().toLowerCase() === formData.website.toLowerCase()
    );
    
    const validSpocDetails = formData.spocDetails.filter(
      spoc => spoc.name?.trim() && spoc.email?.trim() && spoc.phone?.trim() && spoc.location?.trim()
    );
    if (!formData.isExternalCompany && validSpocDetails.length === 0) {
      // setError('At least one complete SPOC with all fields (including location) is required.');
      toast.error('At least one complete SPOC with all fields (including location) is required.');
      return;
    }
    try {
          setAddCompanyBtnDisabled(true)

      console.log('isExternalCompany:', formData.isExternalCompany);
      console.log('validSpocDetails:', validSpocDetails);
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.companyName.trim());
      formDataToSend.append('brandName', formData.brandName.trim());
      // Use default values for external companies
      formDataToSend.append('email', formData.isExternalCompany ? 'na@gmail.com' : formData.email.trim());
      formDataToSend.append('phone', formData.isExternalCompany ? '9876543210' : formData.phone.trim());
      formDataToSend.append('spocDetails', JSON.stringify(validSpocDetails));
      formDataToSend.append('registeredAddress', formData.address.trim());
      formDataToSend.append('operationalAddress', formData.address.trim());
      formDataToSend.append('website', formData.website.trim());
      formDataToSend.append('isExternalCompany', String(formData.isExternalCompany));
      
      console.log('FormData isExternalCompany:', formDataToSend.get('isExternalCompany'));
      console.log('FormData spocDetails:', formDataToSend.get('spocDetails'));

      if (formData.gstTaxNumber?.trim()) {
        formDataToSend.append('gstNo', formData.gstTaxNumber.trim());
      }

      // Handle logo upload
      if (formData.logo && formData.logo instanceof File && formData.logo.size > 0) {
        try {
          // Upload logo file
          const logoUploadResponse = await uploadFile(formData.logo, 'companies/logos');
          
          // Add the returned URL to the form data
          if (logoUploadResponse && logoUploadResponse.fileUrl) {
            formDataToSend.append('logoUrl', logoUploadResponse.fileUrl);
          }
        } catch (uploadError: any) {
          toast.error(`Logo upload failed: ${uploadError.message}`);
          setAddCompanyBtnDisabled(false);
          return;
        }
      }

      // Continue with company creation
      await createCompany(formDataToSend);
      toast.success(`${formData.companyName.trim()} has been successfully onboarded to the platform.`, {
        duration: 5000,
      });
      try {
        const updatedCompaniesResponse = await getAllCompanies();
        if (updatedCompaniesResponse?.data?.companies) {
          const mappedCompanies = updatedCompaniesResponse.data.companies.map((company: any) => ({
            ...company,
            name: company.name || '',
            email: company.email || '',
            phone: company.phone || '',
            operationalAddress: company.operationalAddress || '',
            registeredAddress: company.registeredAddress || '',
            openings: 0,
            candidates: 0,
          }));
          setCompanies(mappedCompanies);
          console.log("Companies list updated successfully");
        }
        setAddCompanyBtnDisabled(false)
      } catch (refreshError) {
        console.error("Failed to refresh companies list:", refreshError);
        console.log('Triggering warning toast: Failed to refresh list');
        toast.error("Company created successfully, but failed to refresh the list. Please reload the page.", {
          duration: 4000,
        });
        window.location.reload();
      }
      setFormData({
        companyName: '',
        address: '',
        email: '',
        phone: '',
        gstTaxNumber: '',
        spocDetails: [{ name: '', email: '', phone: '', location: '' }],
        logo: null,
        website: '',
        brandName: '',
        isExternalCompany: false
      });
      setOpen(false);
      setError(null);
     
    } catch (err: any) {
      console.error('Company creation failed:', err);
      console.log('Triggering error toast: Company creation failed');
      toast.error(err.response?.data?.message || 'Failed to create company. Please try again.', {
        duration: 4000,
      });
      setError(err.response?.data?.message || 'Failed to create company. Please try again.');
      setAddCompanyBtnDisabled(false);
    }
  };

const handleUpdate = (companyId: string,e :any) => {
    e.stopPropagation();
    console.log("Update company with ID:", companyId);
   
    const company = companies.find(c => c.id === companyId);
    if (!company) {
      setError('Company not found');
      console.log('Triggering error toast: Company not found');
      toast.error('Company not found');
      return;
    }
   
    setSelectedCompany(company);
    setUpdateFormData({
      companyName: company.name || '',
      address: company.operationalAddress || company.registeredAddress || '',
      email: company.email || '',
      phone: company.phone || '',
      gstTaxNumber: company.gstNo || '',
      spocDetails: company.spocDetails && company.spocDetails.length > 0
        ? company.spocDetails.map(spoc => ({
            name: spoc.name || '',
            email: spoc.email || '',
            phone: spoc.phone || '',
            location: spoc.location || ''
          }))
        : [{ name: '', email: '', phone: '', location: '' }],
      logo: null,
      website: company.website && company.website !== "N/A" ? company.website : '',
      removeCurrentLogo: false,
      brandName:company.brand || '',
      isExternalCompany: company.isExternalCompany || false
    });
   
    setUpdateOpen(true);
    setError(null);
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) {
      setError('No company selected for update');
      console.log('Triggering error toast: No company selected');
      toast.error('No company selected for update');
      return;
    }
    if (!updateFormData.companyName?.trim()) {
      setError('Company name is required');
      console.log('Triggering error toast: Company name is required');
      toast.error('Company name is required');
      return;
    }
    if (!updateFormData.address?.trim()) {
      setError('Address is required');
      console.log('Triggering error toast: Address is required');
      toast.error('Address is required');
      return;
    }
    if (!updateFormData.email?.trim()) {
      setError('Email is required');
      console.log('Triggering error toast: Email is required');
      toast.error('Email is required');
      return;
    }
    if (!updateFormData.phone?.trim()) {
      setError('Phone is required');
      console.log('Triggering error toast: Phone is required');
      toast.error('Phone is required');
      return;
    }
     if (!updateFormData.brandName?.trim()) {
      setError('Brandname is required');
      console.log('Triggering error toast: Brandname is required');
      toast.error('Brandname is required');
      return;
    }
    if (!updateFormData.website?.trim()) {
      setError('Website is required');
      console.log('Triggering error toast: Website is required');
      toast.error('Website is required');
      return;
    }
    const validSpocDetails = updateFormData.spocDetails.filter(
      spoc => spoc.name?.trim() && spoc.email?.trim() && spoc.phone?.trim() && spoc.location?.trim()
    );

      const duplicateCompany = companies.filter(c=> c.id !== selectedCompany.id).find(
      (c) => {
        // For external companies, skip email/phone validation
        if (updateFormData.isExternalCompany) {
          return c.name.trim().toLowerCase() === updateFormData.companyName.trim().toLowerCase() ||
                 c.brand.trim().toLowerCase() === updateFormData.brandName.toLowerCase() ||
                 c.website.trim().toLowerCase() === updateFormData.website.toLowerCase();
        }
        return c.name.trim().toLowerCase() === updateFormData.companyName.trim().toLowerCase() ||
               c.email.trim().toLowerCase() === updateFormData.email.trim().toLowerCase() ||
               c.phone.trim() === updateFormData.phone.trim() ||
               c.brand.trim().toLowerCase() === updateFormData.brandName.toLowerCase() ||
               c.website.trim().toLowerCase() === updateFormData.website.toLowerCase();
      }
    );
    


    // Only validate SPOC details for non-external companies
    if (!updateFormData.isExternalCompany && validSpocDetails.length === 0) {
      setError('At least one complete SPOC with all fields (including location) is required.');
      console.log('Triggering error toast: At least one complete SPOC required');
      toast.error('At least one complete SPOC with all fields (including location) is required.');
      return;
    }
    try {
      const hasNewLogo = updateFormData.logo && updateFormData.logo instanceof File && updateFormData.logo.size > 0;
      const shouldRemoveLogo = updateFormData.removeCurrentLogo;
      if (hasNewLogo) {
        try {
          // Upload new logo and get the URL
          let logoUploadResponse: any = null;
          let logoUrl: string | undefined = undefined;
          if (updateFormData.logo && updateFormData.logo instanceof File) {
            logoUploadResponse = await uploadFile(updateFormData.logo, 'companies/logos');
            if (logoUploadResponse && logoUploadResponse.fileUrl) {
              logoUrl = logoUploadResponse.fileUrl;
            }
          }

          // Prepare plain object for UpdateCompanyData
          const updatePayload: any = {
            name: updateFormData.companyName.trim(),
            email: updateFormData.email.trim(),
            phone: updateFormData.phone.trim(),
            spocDetails: validSpocDetails,
            registeredAddress: updateFormData.address.trim(),
            operationalAddress: updateFormData.address.trim(),
            brandName: updateFormData.brandName.trim(),
            website: updateFormData.website.trim()
          };

          if (updateFormData.gstTaxNumber?.trim()) {
            updatePayload.gstNo = updateFormData.gstTaxNumber.trim();
          }
          if (logoUrl) {
            updatePayload.logoUrl = logoUrl;
          }

          await updateCompany(selectedCompany.id, updatePayload);
        } catch (uploadError: any) {
          toast.error(`Logo upload failed: ${uploadError.message}`);
          return;
        }
      } else {
        const updatePayload: any = {
          name: updateFormData.companyName.trim(),
          email: updateFormData.isExternalCompany ? 'na@gmail.com' : updateFormData.email.trim(),
          phone: updateFormData.isExternalCompany ? '9876543210' : updateFormData.phone.trim(),
          spocDetails: validSpocDetails,
          registeredAddress: updateFormData.address.trim(),
          operationalAddress: updateFormData.address.trim(),
          brandName: updateFormData.brandName.trim(),
          website:  updateFormData.website.trim(),
          isExternalCompany: updateFormData.isExternalCompany
        };

        if (updateFormData.gstTaxNumber?.trim()) {
          updatePayload.gstNo = updateFormData.gstTaxNumber.trim();
        }

        if (shouldRemoveLogo) {
          updatePayload.logoUrl = null;
        }

        await updateCompany(selectedCompany.id, updatePayload);
      }
      console.log('Triggering success toast: Company updated');
      toast.success(`${updateFormData.companyName.trim()} has been successfully updated.`, {
        duration: 4000,
      });
      console.log("Company updated successfully, refreshing list...");
     
      try {
        const updatedCompaniesResponse = await getAllCompanies();
       
        if (updatedCompaniesResponse?.data?.companies) {
          const mappedCompanies = updatedCompaniesResponse.data.companies.map((company: any) => ({
            ...company,
            name: company.name || '',
            email: company.email || '',
            phone: company.phone || '',
            operationalAddress: company.operationalAddress || '',
            registeredAddress: company.registeredAddress || '',
            openings: 0,
            candidates: 0,
          }));
          setCompanies(mappedCompanies);
          console.log("Companies list updated successfully");
        }
      } catch (refreshError) {
        console.error("Failed to refresh companies list:", refreshError);
        console.log('Triggering error toast: Failed to refresh list');
        toast.error("Company updated successfully, but failed to refresh the list. Please reload the page.", {
          duration: 4000,
        });
        window.location.reload();
      }
      setUpdateFormData({
        companyName: '',
        address: '',
        email: '',
        phone: '',
        gstTaxNumber: '',
        spocDetails: [{ name: '', email: '', phone: '', location: '' }],
        logo: null,
        website: '',
        removeCurrentLogo: false,
        brandName: '',
        isExternalCompany: false
      });
      setSelectedCompany(null);
      setUpdateOpen(false);
      setError(null);
     
      console.log("Update form reset and modal closed");
    } catch (err: any) {
      console.error('Company update failed:', err);
      console.log('Triggering error toast: Company update failed');
      toast.error(err.response?.data?.message || 'Failed to update company. Please try again.', {
        duration: 4000,
      });
      setError(err.response?.data?.message || 'Failed to update company. Please try again.');
    }
  };

  const handleUpdateInputChange = (field: string, value: string) => {
    setUpdateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateSpocChange = (index: number, field: string, value: string) => {
    setUpdateFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.map((spoc, i) =>
        i === index ? { ...spoc, [field]: value } : spoc
      )
    }));
  };

  const addUpdateSpoc = () => {
    setUpdateFormData(prev => ({
      ...prev,
      spocDetails: [...prev.spocDetails, { name: '', email: '', phone: '', location: '' }]
    }));
  };

  const removeUpdateSpoc = (index: number) => {
    if (updateFormData.spocDetails.length > 1) {
      setUpdateFormData(prev => ({
        ...prev,
        spocDetails: prev.spocDetails.filter((_, i) => i !== index)
      }));
    }
  };

  const handleUpdateLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedCompany?.id) { // Make sure we have the company ID
      try {
        const loadingToast = toast.loading('Uploading logo...');
        
        // Validate file
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
          toast.dismiss(loadingToast);
          toast.error('Invalid file type. Only JPG, PNG, GIF, and WebP images are allowed.');
          e.target.value = ''; // Reset file input
          return;
        }

        if (file.size > maxSize) {
          toast.dismiss(loadingToast);
          toast.error('File size exceeds 5MB limit');
          e.target.value = ''; // Reset file input
          return;
        }

        // Use company ID in the folder path
        const folderPath = `company/${selectedCompany.id}/logo`;

        // Upload file with the new folder path
        const response = await uploadFile(file, folderPath);
        
        if (response && response.fileUrl) {
          // Update form data with the uploaded file URL
          setUpdateFormData(prev => ({
            ...prev,
            logo: file,
            logoUrl: response.fileUrl,
            removeCurrentLogo: false
          }));
          
          toast.dismiss(loadingToast);
          toast.success('Logo uploaded successfully');
        }
      } catch (error: any) {
        toast.error(`Failed to upload logo: ${error.message}`);
        e.target.value = ''; // Reset file input
      }
    }
  };

  // Implement delete functionality (assuming you have a deleteCompany API)
  const handleDelete = async (companyId: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        // Assuming you have a deleteCompany function in your companiesapi
        // await deleteCompany(companyId);
        console.log('Triggering success toast: Company deleted');
        toast.success('Company deleted successfully');
        const updatedCompaniesResponse = await getAllCompanies();
        if (updatedCompaniesResponse?.data?.companies) {
          setCompanies(updatedCompaniesResponse.data.companies);
        }
      } catch (err: any) {
        console.error('Company deletion failed:', err);
        console.log('Triggering error toast: Company deletion failed');
        toast.error(err.response?.data?.message || 'Failed to delete company. Please try again.');
      }
    }
  };

  // Placeholder implementations for action handlers
  const handleViewOpenings = (companyId: string) => {
    console.log("View openings for company:", companyId);
    // Implement navigation or modal for viewing openings
  };

  const handleviewApplications = (companyId: string) => {
    console.log("View Applicationss for company:", companyId);
    // Implement navigation or modal for viewing candidates
  };

  const handleViewEarnings = (companyId: string) => {
    console.log("View earnings for company:", companyId);
    // Implement navigation or modal for viewing earnings
  };

  // if (loading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-background">
  //       <div className="text-center space-y-4">
  //         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
  //         <p className="text-lg text-muted-foreground">Loading companies...</p>
  //       </div>
  //     </div>
  //   );
  // }


  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 w-full px-6 py-6">
      <div  className="flex items-center gap-4">
          {/* Back Button */}
          <div>
            <Button
              variant="ghost"
              size="lg"
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              onClick={() => navigate(-1)}
            >
              <ArrowLeftCircle className="h-9 w-9 text-gray-600" style={{width: "30px", height: "30px"}}/>
            </Button>
          </div>
          
          {/* Title Section */}
          <div className="flex-grow" style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
           <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Companies</h1>
            </div>
           <div>
   <Button 
     className="bg-orange-600 text-white hover:bg-orange-700" 
     onClick={() => setOpen(true)}
   >
      <Plus className="mr-2 h-4 w-4" /> Add Company
   </Button>
</div>
          </div>
        </div>


      <div className="container mx-auto px-4 py-6 lg:px-6 xl:px-8">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
        
          <div className="flex gap-2">
            <Dialog open={selectionListOpen} onOpenChange={(open) => {
              setSelectionListOpen(open);
              if (!open) {
                setSelectionListError(null);
                setSelectionSearchTerm('');
                setAllCompanySelectionList([]);
                setCompanySelectionList([]);
              }
            }}>
              <DialogTrigger asChild>
              
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-foreground">Company Selection List</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search companies..."
                      value={selectionSearchTerm}
                      onChange={(e) => setSelectionSearchTerm(e.target.value)}
                      className="pl-10 border-input focus:border-orange-300 focus:ring-orange-200"
                      disabled={selectionListLoading}
                    />
                  </div>
                  
                  {selectionListLoading && (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mr-3"></div>
                      <p className="text-muted-foreground">Loading company list...</p>
                    </div>
                  )}
                  
                  {selectionListError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm border border-red-200 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Error loading companies</p>
                        <p className="text-xs mt-1">{selectionListError}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={fetchCompanySelectionList}
                        className="ml-auto text-red-600 hover:bg-red-100"
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                  
                  {!selectionListLoading && !selectionListError && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                        <span>
                          {selectionSearchTerm ? 
                            `Found ${companySelectionList.length} of ${allCompanySelectionList.length} companies` :
                            `Showing ${companySelectionList.length} companies`
                          }
                        </span>
                        <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                          {companySelectionList.length} Results
                        </Badge>
                      </div>
                      
                      <div className="grid gap-3 max-h-96 overflow-y-auto">
                        {companySelectionList.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <div className="flex flex-col items-center space-y-2">
                              <Search className="h-8 w-8 text-muted-foreground/50" />
                              <p className="text-sm">
                                {selectionSearchTerm ? 
                                  `No companies found matching "${selectionSearchTerm}".` : 
                                  'No companies available.'
                                }
                              </p>
                              {selectionSearchTerm && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectionSearchTerm('')}
                                  className="text-orange-600 hover:bg-orange-50"
                                >
                                  Clear search
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : (
                          companySelectionList.map((company) => (
                            <Card key={company.id} className="hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 hover:border-orange-300">
                              <CardContent className="p-4">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    {company.logoUrl ? (
                                      <img
                                        src={company.logoUrl}
                                        
                                        alt={`${company.name} logo`}
                                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const fallback = target.nextElementSibling as HTMLDivElement;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                    
                                    ) : null}
                                    <div
                                      className={`w-10 h-10 rounded-full bg-orange-100 text-orange-600 font-semibold text-sm flex items-center justify-center ${company.logoUrl ? 'hidden' : 'flex'}`}
                                    >
                                      {company.name.charAt(0).toUpperCase()}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-foreground truncate">
                                        {company.name}
                                      </p>
                                      {company.isExternalCompany && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                          External
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      ID: {company.id}
                                    </p>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <Badge variant="outline" className="text-xs border-green-200 text-green-700">
                                      Available
                                    </Badge>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-4 border-t">
                    <div className="text-xs text-muted-foreground">
                      {selectionSearchTerm && (
                        <span>Search: "{selectionSearchTerm}"</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {selectionSearchTerm && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectionSearchTerm('')}
                          className="text-orange-600 hover:bg-orange-50"
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setSelectionListOpen(false)}
                        className="border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={open} onOpenChange={(open) => { 
              setOpen(open); 
              if (!open) {
                setError(null);
              }
              setAddCompanyBtnDisabled(false);
            }}>
              <DialogTrigger asChild>
               
              </DialogTrigger>
              <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-2xl font-bold text-foreground">Add New Company</DialogTitle>
                    <div className="flex items-center gap-2 pr-4">
                       {formData.isExternalCompany && (
                        <Label htmlFor="isEx-toggle" className="text-sm font-medium text-orange-600 cursor-pointer">
                          External Company
                        </Label>
                      )}
                       <Switch
                        id="isEx-toggle"
                        checked={formData.isExternalCompany}
                        onCheckedChange={(checked) => setFormData({ ...formData, isExternalCompany: checked })}
                        className="data-[state=checked]:bg-orange-500"
                      />
                   
                    </div>
                  </div>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6 p-4">
                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm border border-red-200">
                      {error}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-sm font-medium text-foreground">
                        Company Name (legal name)<span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter company name"
                      />
                    </div>

                     <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-sm font-medium text-foreground">
                        Brand Name<span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="BrandName"
                        name="brandName"
                        value={formData.brandName}
                        onChange={handleChange}
                        required
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter Brand name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-sm font-medium text-foreground">
                        Address <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter address"
                      />
                    </div>
                    {!formData.isExternalCompany && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-sm font-medium text-foreground">
                            Email <span className="text-red-600">*</span>
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                            placeholder="Enter email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                            Phone <span className="text-red-600">*</span>
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                            placeholder="Enter phone number"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="gstTaxNumber" className="text-sm font-medium text-foreground">
                            GST Number (Optional)
                          </Label>
                          <Input
                            id="gstTaxNumber"
                            name="gstTaxNumber"
                            value={formData.gstTaxNumber}
                            onChange={handleChange}
                            className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                            placeholder="Enter GST Taxnumber"
                          />
                        </div>
                      </>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-sm font-medium text-foreground">
                        Website <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter website URL"
                      />
                    </div>
                  </div>
                  {!formData.isExternalCompany && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-foreground">
                          SPOC Details <span className="text-red-600">*</span> (at least one required)
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addSpoc}
                          className="border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add SPOC
                        </Button>
                      </div>
                      {formData.spocDetails.map((spoc, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 p-4 rounded-md bg-gray-50 relative"
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 text-gray-500 hover:text-red-600 transition-colors"
                            onClick={() => removeSpoc(index)}
                            disabled={formData.spocDetails.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor={`spocName-${index}`} className="text-sm font-medium text-foreground">
                                SPOC Name
                              </Label>
                              <Input
                                id={`spocName-${index}`}
                                name="name"
                                value={spoc.name}
                                onChange={(e) => handleChange(e, index)}
                                className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                                placeholder="Enter SPOC name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`spocEmail-${index}`} className="text-sm font-medium text-foreground">
                                SPOC Email
                              </Label>
                              <Input
                                id={`spocEmail-${index}`}
                                name="email"
                                type="email"
                                value={spoc.email}
                                onChange={(e) => handleChange(e, index)}
                                className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                                placeholder="Enter SPOC email"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`spocPhone-${index}`} className="text-sm font-medium text-foreground">
                                SPOC Phone
                              </Label>
                              <Input
                                id={`spocPhone-${index}`}
                                name="phone"
                                type="tel"
                                value={spoc.phone}
                                onChange={(e) => handleChange(e, index)}
                                className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                                placeholder="Enter SPOC phone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`spocLocation-${index}`} className="text-sm font-medium text-foreground">
                                SPOC Location
                              </Label>
                              <Input
                                id={`spocLocation-${index}`}
                                name="location"
                                value={spoc.location}
                                onChange={(e) => handleChange(e, index)}
                                className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                                placeholder="Enter SPOC location"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="space-y-3 pt-2">
                    <Label htmlFor="logo" className="text-sm font-medium text-foreground">
                      Logo (Recommended: 500px x 500px)
                    </Label>
                    <div className="space-y-2">
                      <Input
                        id="logo"
                        name="logo"
                        type="file"
                        accept="image/*"
                        onChange={handleChange}
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md file:text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-orange-50 file:text-sm file:font-medium hover:file:bg-orange-100 transition-colors h-12"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Upload a square logo for best results. Supported formats: JPG, PNG, SVG
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setOpen(false)}
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                      disabled={addCompanyBtnDisabled}
                    >
                      Save
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <Card className="mb-6 shadow-sm">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search companies, emails, or addresses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-input focus:border-orange-300 focus:ring-orange-200"
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={sortBy} onValueChange={(value) => setSortBy(value)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="createdAt">Created Date</SelectItem>
                      <SelectItem value="name">Company Name</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select value={companyTypeFilter} onValueChange={handleCompanyTypeChange}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Company type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      <SelectItem value="internal">Internal Companies</SelectItem>
                      <SelectItem value="external">External Companies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center gap-2"
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                  {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                </Button>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <span>Showing {indexOfFirstCompany + 1}-{Math.min(indexOfLastCompany, filteredCompanies.length)} of {filteredCompanies.length} companies</span>
              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                {filteredCompanies.length} Results
              </Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="font-semibold text-foreground">Company Name</TableHead>
                    <TableHead className="font-semibold text-foreground">Brand</TableHead>
                    <TableHead className="font-semibold text-foreground hidden lg:table-cell">Address</TableHead>
                    <TableHead className="font-semibold text-foreground">Email</TableHead>
                    <TableHead className="font-semibold text-foreground hidden md:table-cell">SPOC Email</TableHead>
                    <TableHead className="font-semibold text-foreground hidden md:table-cell">Onboarded By</TableHead>
                    <TableHead className="font-semibold text-foreground hidden xl:table-cell">Created At</TableHead>
                    <TableHead className="font-semibold text-foreground text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!(selectionListLoading|| loading) && (companies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchTerm ? "No companies found matching your search." : "No companies available."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => (
                      <TableRow key={company.id} onClick={() => navigate(`/companies/${company.id}`)} className="hover:bg-muted/20 transition-colors cursor-pointer">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            {company.logoUrl ? (
                              <img
                                src={company.logoUrl}
                                alt={`${company.name} logo`}
                                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  // Only show fallback if logo load fails
                                  const fallback = target.nextElementSibling as HTMLDivElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 font-semibold flex items-center justify-center">
                                <img 
                                  src="/path/to/default-company-logo.png" 
                                  alt="Default company logo"
                                  className="w-full h-full object-cover rounded-full"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    // Show first letter as last fallback
                                    const parent = target.parentElement;
                                    if (parent) {
                                      parent.innerHTML = company.name.charAt(0).toUpperCase();
                                    }
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="text-orange-600 font-semibold">
                                  {company.name}
                                </span>
                                {company.isExternalCompany && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                    External
                                  </Badge>
                                )}
                              </div>
                              <span className="lg:hidden text-xs text-muted-foreground mt-1 line-clamp-2">
                                {company.operationalAddress || company.registeredAddress || 'No address provided'}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="max-w-xs">
                            <p className="text-sm text-muted-foreground line-clamp-2">
                             {company.brand || '-'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div className="max-w-xs">
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {company.operationalAddress || company.registeredAddress || 'No address provided'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="text-sm text-foreground truncate">
                              {company.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="max-w-xs">
                            {company.spocDetails && company.spocDetails.length > 0 ? (
                              <div className="space-y-1">
                                {company.spocDetails.slice(0, 2).map((spoc, index) => (
                                  <p key={index} className="text-xs text-muted-foreground truncate">
                                    {spoc.email}
                                  </p>
                                ))}
                                {company.spocDetails.length > 2 && (
                                  <p className="text-xs text-muted-foreground">
                                    +{company.spocDetails.length - 2} more
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">No SPOC</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <p className="text-sm text-muted-foreground">
                            {company?.onboardedBy?.username || '-'}
                          </p>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <p className="text-sm text-muted-foreground">
                            {formatDate(company.createdAt)}
                          </p>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleUpdate(company.id, e)}
                              className="h-8 w-8 p-0 hover:bg-orange-50 hover:text-orange-600"
                              title="Edit Company"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                         
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ))}
                  {
                    (selectionListLoading || loading) && (
                      <>
                        {[...Array(10)].map((_, index) => (
                          <TableRow key={`shimmer-${index}`} className="bg-white">
                            {/* Company Name */}
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="animate-pulse bg-gray-200 rounded-full w-10 h-10"></div>
                                <div className="flex flex-col">
                                  <div className="animate-pulse bg-gray-200 rounded w-32 h-4 mb-2"></div>
                                  <div className="animate-pulse bg-gray-200 rounded w-40 h-3"></div>
                                </div>
                              </div>
                            </TableCell>
                            {/* Address */}
                            <TableCell className="hidden lg:table-cell">
                              <div className="max-w-xs">
                                <div className="animate-pulse bg-gray-200 rounded w-full h-4 mb-1"></div>
                                <div className="animate-pulse bg-gray-200 rounded w-3/4 h-3"></div>
                              </div>
                            </TableCell>
                            {/* Email */}
                            <TableCell>
                              <div className="max-w-xs">
                                <div className="animate-pulse bg-gray-200 rounded w-full h-4"></div>
                              </div>
                            </TableCell>
                            {/* SPOC Email */}
                            <TableCell className="hidden md:table-cell">
                              <div className="max-w-xs">
                                <div className="space-y-1">
                                  <div className="animate-pulse bg-gray-200 rounded w-full h-3"></div>
                                  <div className="animate-pulse bg-gray-200 rounded w-2/3 h-3"></div>
                                </div>
                              </div>
                            </TableCell>
                            {/* Created At */}
                            <TableCell className="hidden xl:table-cell">

                                <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                            </TableCell>
                            {/* Actions */}
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <div className="animate-pulse bg-gray-200 rounded w-8 h-8"></div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )
                  }
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
          <div className="flex items-center gap-2 justify-end p-[20px]">
        {renderPagination()}
      </div>
        <Dialog open={updateOpen} onOpenChange={(open) => { setUpdateOpen(open); if (!open) setError(null); }}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-foreground">Update Company</DialogTitle>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={updateFormData.isExternalCompany}
                    onCheckedChange={(checked) => setUpdateFormData(prev => ({ ...prev, isExternalCompany: checked }))}
                    className="data-[state=checked]:bg-orange-500"
                  />
                  {updateFormData.isExternalCompany && (
                    <span className="text-sm font-medium text-orange-600">External Company</span>
                  )}
                </div>
              </div>
            </DialogHeader>
            <form onSubmit={handleUpdateSubmit} className="space-y-6 p-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm border border-red-200">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-foreground">
                    Company Name (legal name) <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={updateFormData.companyName}
                    onChange={(e) => handleUpdateInputChange('companyName', e.target.value)}
                    required
                    className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                    placeholder="Enter company name"
                  />
                </div>

                 <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-foreground">
                    Brand Name <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="brandName"
                    name="brandName"
                    value={updateFormData.brandName}
                    onChange={(e) => handleUpdateInputChange('brandName', e.target.value)}
                    required
                    className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                    placeholder="Enter brand name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-sm font-medium text-foreground">
                    Address <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={updateFormData.address}
                    onChange={(e) => handleUpdateInputChange('address', e.target.value)}
                    required
                    className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                    placeholder="Enter address"
                  />
                </div>
                {!updateFormData.isExternalCompany && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-foreground">
                        Email <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={updateFormData.email}
                        onChange={(e) => handleUpdateInputChange('email', e.target.value)}
                        required
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-foreground">
                        Phone <span className="text-red-600">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={updateFormData.phone}
                        onChange={(e) => handleUpdateInputChange('phone', e.target.value)}
                        required
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gstTaxNumber" className="text-sm font-medium text-foreground">
                        GST Tax Number (Optional)
                      </Label>
                      <Input
                        id="gstTaxNumber"
                        name="gstTaxNumber"
                        value={updateFormData.gstTaxNumber}
                        onChange={(e) => handleUpdateInputChange('gstTaxNumber', e.target.value)}
                        className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                        placeholder="Enter GST number"
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="website" className="text-sm font-medium text-foreground">
                    Website <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="website"
                    name="website"
                    value={updateFormData.website}
                    onChange={(e) => handleUpdateInputChange('website', e.target.value)}
                    className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                    placeholder="Enter website URL"
                  />
                </div>
              </div>
              {!updateFormData.isExternalCompany && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-foreground">
                      SPOC Details <span className="text-red-600">*</span> (at least one required)
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addUpdateSpoc}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add SPOC
                    </Button>
                  </div>
                  {updateFormData.spocDetails.map((spoc, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 p-4 rounded-md bg-gray-50 relative"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 text-gray-500 hover:text-red-600 transition-colors"
                      onClick={() => removeUpdateSpoc(index)}
                      disabled={updateFormData.spocDetails.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`spocName-${index}`} className="text-sm font-medium text-foreground">
                          SPOC Name
                        </Label>
                        <Input
                          id={`spocName-${index}`}
                          name="name"
                          value={spoc.name}
                          onChange={(e) => handleUpdateSpocChange(index, 'name', e.target.value)}
                          className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                          placeholder="Enter SPOC name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`spocEmail-${index}`} className="text-sm font-medium text-foreground">
                          SPOC Email
                        </Label>
                        <Input
                          id={`spocEmail-${index}`}
                          name="email"
                          type="email"
                          value={spoc.email}
                          onChange={(e) => handleUpdateSpocChange(index, 'email', e.target.value)}
                          className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                          placeholder="Enter SPOC email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`spocPhone-${index}`} className="text-sm font-medium text-foreground">
                          SPOC Phone
                        </Label>
                        <Input
                          id={`spocPhone-${index}`}
                          name="phone"
                          type="tel"
                          value={spoc.phone}
                          onChange={(e) => handleUpdateSpocChange(index, 'phone', e.target.value)}
                          className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                          placeholder="Enter SPOC phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`spocLocation-${index}`} className="text-sm font-medium text-foreground">
                          SPOC Location
                        </Label>
                        <Input
                          id={`spocLocation-${index}`}
                          name="location"
                          value={spoc.location}
                          onChange={(e) => handleUpdateSpocChange(index, 'location', e.target.value)}
                          className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md transition-colors"
                          placeholder="Enter SPOC location"
                        />
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}
              <div className="space-y-3 pt-2">
                <Label htmlFor="logo" className="text-sm font-medium text-foreground">
                  Logo (Optional - Recommended: 500px x 500px)
                </Label>
                <div className="space-y-2">
                  <Input
                    id="logo"
                    name="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleUpdateLogoChange}
                    className="border-input focus:border-orange-500 focus:ring-orange-200 rounded-md file:text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-orange-50 file:text-sm file:font-medium hover:file:bg-orange-100 transition-colors h-12"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload a square logo for best results. Supported formats: JPG, PNG, SVG
                  </p>
                </div>
                {selectedCompany?.logoUrl && (
                  <div className="flex items-center space-x-2 mt-2">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>Current logo:</span>
                      <img
                        src={selectedCompany.logoUrl}
                        alt="Current logo"
                        className="w-8 h-8 rounded object-cover border border-gray-200"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setUpdateFormData(prev => ({ ...prev, removeCurrentLogo: !prev.removeCurrentLogo }))}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50 text-xs"
                    >
                      {updateFormData.removeCurrentLogo ? 'Keep Current' : 'Remove Current'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setUpdateOpen(false)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                >
                  Save
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Companies;
